import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, fmtUsd, fmtNum } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Users, Video, RefreshCw, Radio, Trash2, Lock, Unlock, Landmark, CheckCircle2, Mic, History, Share2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const EscrowBadge = ({ status, t }) => {
  const map = {
    held: { label: t("escrow_held"), cls: "bg-amber-500/15 text-amber-400 border-amber-700/50" },
    pending_release: { label: t("escrow_pending_release"), cls: "bg-sky-500/15 text-sky-400 border-sky-700/50" },
    disputed: { label: t("escrow_disputed"), cls: "bg-red-500/15 text-red-400 border-red-800/50" },
    released: { label: t("escrow_released"), cls: "bg-emerald-500/15 text-emerald-400 border-emerald-700/50" },
    pending: { label: t("escrow_pending"), cls: "bg-zinc-700/40 text-zinc-400 border-zinc-700" },
    refunded: { label: t("escrow_refunded"), cls: "bg-red-500/15 text-red-400 border-red-800/50" },
  };
  const m = map[status] || map.pending;
  return <Badge variant="outline" className={`${m.cls} text-xs`}>{m.label}</Badge>;
};

const PayoutBadge = ({ status, t }) => {
  if (!status) return null;
  const map = {
    transferred: { label: t("payout_transferred"), cls: "bg-emerald-500/15 text-emerald-400 border-emerald-700/50" },
    pending_transfer: { label: t("payout_pending"), cls: "bg-zinc-700/40 text-zinc-400 border-zinc-700" },
    transfer_failed: { label: t("payout_failed"), cls: "bg-red-500/15 text-red-400 border-red-800/50" },
  };
  const m = map[status];
  if (!m) return null;
  return <Badge variant="outline" className={`${m.cls} text-xs`}>{m.label}</Badge>;
};

export default function StreamerDashboard() {
  const [channel, setChannel] = useState(undefined);
  const [earnings, setEarnings] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [connect, setConnect] = useState(null);
  const [queue, setQueue] = useState([]);
  const [proofMap, setProofMap] = useState({});
  const [history, setHistory] = useState([]);
  const [fee, setFee] = useState(30);
  const [tierForm, setTierForm] = useState({ name: "", description: "", amount_usd: "" });
  const [verifying, setVerifying] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();

  const shareProfile = async () => {
    const url = `${window.location.origin}/streamer/${user.id}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    toast.success(t("dash_share_copied"));
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/channels/me");
      setChannel(data);
    } catch {
      setChannel(null);
      return;
    }
    const [e, tr, c, cfg, q, h] = await Promise.all([
      api.get("/streamers/me/earnings"), api.get("/tiers/my"),
      api.get("/connect/status"), api.get("/config/requirements"),
      api.get("/streams/queue"), api.get("/streams/history"),
    ]);
    setEarnings(e.data);
    setTiers(tr.data);
    setConnect(c.data);
    setFee(cfg.data.platform_fee_percent);
    setQueue(q.data);
    setHistory(h.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const id = setInterval(() => {
      api.get("/streams/queue").then(({ data }) => setQueue(data)).catch((e) => console.debug("Queue poll failed", e));
    }, 15000);
    return () => clearInterval(id);
  }, []);

  const completeMic = async (sessionId) => {
    try {
      await api.post(`/streams/queue/${sessionId}/complete`, { proof_url: (proofMap[sessionId] || "").trim() });
      toast.success(t("queue_done_toast"));
      const [{ data: q }, { data: e }] = await Promise.all([
        api.get("/streams/queue"), api.get("/streamers/me/earnings"),
      ]);
      setQueue(q);
      setEarnings(e);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const refreshChannel = async () => {
    const { data } = await api.post("/channels/refresh");
    setChannel(data);
    toast.success(t("dash_channel_updated"));
  };

  const verifyStream = async () => {
    setVerifying(true);
    try {
      const { data } = await api.post("/streams/verify");
      if (data.is_live) {
        toast.success(t("dash_verify_ok"));
        load();
      } else {
        toast.error(t("dash_verify_fail"));
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setVerifying(false);
    }
  };

  const setupPayouts = async () => {
    setConnecting(true);
    try {
      const { data } = await api.post("/connect/onboard", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      toast.error(msg.length > 140 ? msg.slice(0, 140) + "…" : msg);
      setConnecting(false);
    }
  };

  const createTier = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tiers", { ...tierForm, amount_usd: parseFloat(tierForm.amount_usd) });
      setTierForm({ name: "", description: "", amount_usd: "" });
      toast.success(t("dash_tier_created"));
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const deleteTier = async (id) => {
    await api.delete(`/tiers/${id}`);
    toast.success(t("dash_tier_deleted"));
    load();
  };

  if (channel === undefined) return <div className="p-16 text-center text-zinc-500">{t("loading")}</div>;

  if (channel === null) {
    return (
      <div data-testid="streamer-dashboard-nochannel" className="max-w-2xl mx-auto px-6 py-24 text-center">
        <h1 className="text-4xl font-black tracking-tighter text-white mb-4">{t("dash_no_channel_title")}</h1>
        <p className="text-zinc-400 mb-8">{t("dash_no_channel_sub")}</p>
        <Button data-testid="link-channel-cta-btn" onClick={() => navigate("/vincular")}
          className="rounded-full bg-emerald-600 hover:bg-emerald-500 text-white px-8 h-12">
          {t("dash_link_cta")}
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="streamer-dashboard" className="max-w-7xl mx-auto px-6 py-12 space-y-10">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-4xl font-black tracking-tighter text-white">{channel.display_name}</h1>
            {channel.is_live && (
              <span data-testid="live-badge" className="live-pulse inline-flex items-center gap-1.5 bg-red-500 text-white text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full">
                <Radio size={12} /> {t("live")}
              </span>
            )}
          </div>
          <p className="text-sm text-zinc-400 mt-2 capitalize">
            {channel.platform} · @{channel.handle} · {channel.verified ?
              <span className="text-emerald-400 font-semibold">{t("dash_verified")}</span> :
              <span className="text-red-400 font-semibold">{t("dash_not_verified")}</span>}
            {channel.data_source === "mock" && <span className="text-amber-400/70 ml-2">{t("dash_mock")}</span>}
          </p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <Button data-testid="share-profile-btn" variant="outline" onClick={shareProfile}
            className="rounded-full border-emerald-800/60 text-emerald-400 hover:bg-emerald-950/40 hover:text-emerald-300 bg-transparent">
            <Share2 size={15} className="mr-2" /> {t("dash_share")}
          </Button>
          <Button data-testid="refresh-channel-btn" variant="outline" onClick={refreshChannel}
            className="rounded-full border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white bg-transparent">
            <RefreshCw size={15} className="mr-2" /> {t("dash_refresh")}
          </Button>
          <Button data-testid="verify-stream-btn" onClick={verifyStream} disabled={verifying}
            className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold">
            <Radio size={15} className="mr-2" /> {verifying ? t("dash_verifying") : t("dash_verify_stream")}
          </Button>
        </div>
      </div>

      <div data-testid="share-link-card" className="bg-emerald-950/20 border border-emerald-800/40 rounded-2xl p-5 mb-8">
        <div className="flex items-center gap-2 text-emerald-400 text-xs uppercase tracking-widest font-bold mb-2">
          <Share2 size={14} /> {t("dash_share_title")}
        </div>
        <p className="text-xs text-zinc-400 mb-3">{t("dash_share_hint")}</p>
        <div className="flex items-center gap-2 flex-wrap">
          <code data-testid="share-link-url" className="flex-1 min-w-0 truncate bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-emerald-300">
            {`${window.location.origin}/streamer/${user.id}`}
          </code>
          <Button data-testid="share-link-copy-btn" onClick={shareProfile}
            className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold shrink-0">
            <Share2 size={14} className="mr-1.5" /> {t("dash_share_copy")}
          </Button>
        </div>
      </div>


      <p className="text-xs text-zinc-500 -mt-6">{t("dash_auto_note")}</p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div data-testid="stat-followers" className="bg-zinc-900 border border-zinc-800/60 rounded-2xl p-6">
          <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-widest font-bold mb-3"><Users size={14} /> {t("dash_followers")}</div>
          <p className="text-3xl font-black text-white">{fmtNum(channel.followers)}</p>
        </div>
        <div data-testid="stat-videos" className="bg-zinc-900 border border-zinc-800/60 rounded-2xl p-6">
          <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-widest font-bold mb-3"><Video size={14} /> {t("dash_videos")}</div>
          <p className="text-3xl font-black text-white">{fmtNum(channel.videos)}</p>
        </div>
        <div data-testid="stat-held" className="bg-zinc-900 border border-amber-800/40 rounded-2xl p-6">
          <div className="flex items-center gap-2 text-amber-500 text-xs uppercase tracking-widest font-bold mb-3"><Lock size={14} /> {t("dash_held")}</div>
          <p className="text-3xl font-black text-amber-400">{earnings ? fmtUsd(earnings.held_cents) : "—"}</p>
        </div>
        <div data-testid="stat-pending-release" className="bg-zinc-900 border border-sky-800/40 rounded-2xl p-6">
          <div className="flex items-center gap-2 text-sky-400 text-xs uppercase tracking-widest font-bold mb-3"><History size={14} /> {t("dash_pending_release")}</div>
          <p className="text-3xl font-black text-sky-400">{earnings ? fmtUsd(earnings.pending_release_cents || 0) : "—"}</p>
        </div>
        <div data-testid="stat-released" className="bg-zinc-900 border border-emerald-800/40 rounded-2xl p-6">
          <div className="flex items-center gap-2 text-emerald-500 text-xs uppercase tracking-widest font-bold mb-3"><Unlock size={14} /> {t("dash_released", { s: 100 - fee })}</div>
          <p className="text-3xl font-black text-emerald-400">{earnings ? fmtUsd(earnings.released_cents) : "—"}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 bg-zinc-900 border border-zinc-800/60 rounded-2xl p-8">
          <h3 className="text-2xl font-semibold text-white mb-6">{t("dash_payments")}</h3>
          {earnings && earnings.transactions.length > 0 ? (
            <div className="space-y-3">
              {earnings.transactions.map((tx) => (
                <div key={tx.session_id} data-testid="earning-row" className="flex items-center justify-between border-b border-zinc-800/60 pb-3">
                  <div>
                    <p className="text-sm font-semibold text-white">{tx.tier_name} <span className="text-zinc-500 font-normal">{t("dash_from")} {tx.fan_name}</span></p>
                    <p className="text-xs text-zinc-500">{new Date(tx.created_at).toLocaleString(lang)} · {t("dash_your_share")} {fmtUsd(tx.streamer_share_cents)}</p>
                    {tx.fan_message && (
                      <p data-testid="fan-message-display" className="text-xs text-emerald-300/80 italic mt-1">“{tx.fan_message}”</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    <span className="text-sm font-bold text-white">{fmtUsd(tx.amount_cents)}</span>
                    {tx.payment_status === "paid" ? (
                      <>
                        <EscrowBadge status={tx.escrow_status} t={t} />
                        {tx.escrow_status === "released" && <PayoutBadge status={tx.payout_status} t={t} />}
                      </>
                    ) : (
                      <Badge variant="outline" className="bg-zinc-800 text-zinc-400 border-zinc-700 text-xs">
                        {{ pending: t("st_pending"), failed: t("st_failed"), expired: t("st_expired") }[tx.payment_status] || tx.payment_status}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-zinc-500">{t("dash_no_payments")}</p>
          )}
        </div>

        <div className="space-y-8">
          <div data-testid="payouts-card" className="bg-zinc-900 border border-zinc-800/60 rounded-2xl p-8">
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <Landmark size={18} className="text-emerald-400" /> {t("payouts_title")}
            </h3>
            {connect?.payouts_enabled ? (
              <div className="flex items-start gap-3">
                <CheckCircle2 size={18} className="text-emerald-400 mt-0.5 shrink-0" />
                <p className="text-sm text-zinc-300 leading-relaxed">{t("payouts_active")}</p>
              </div>
            ) : (
              <>
                <p className="text-sm text-zinc-400 leading-relaxed mb-5">
                  {connect?.connected ? t("payouts_incomplete") : t("payouts_none", { s: 100 - fee })}
                </p>
                <Button data-testid="setup-payouts-btn" onClick={setupPayouts} disabled={connecting}
                  className="w-full rounded-full bg-emerald-600 hover:bg-emerald-500 text-white">
                  {connecting ? t("payouts_redirecting") : connect?.connected ? t("payouts_continue") : t("payouts_setup")}
                </Button>
              </>
            )}
          </div>

          <div className="bg-zinc-900 border border-zinc-800/60 rounded-2xl p-8">
            <h3 className="text-2xl font-semibold text-white mb-6">{t("dash_tiers")}</h3>
            <div className="space-y-3 mb-6">
              {tiers.map((tier) => (
                <div key={tier.id} data-testid="tier-row" className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded-xl p-4">
                  <div>
                    <p className="text-sm font-semibold text-white">{tier.name}</p>
                    <p className="text-xs text-zinc-500">{fmtUsd(tier.amount_cents)}</p>
                  </div>
                  <button data-testid="delete-tier-btn" onClick={() => deleteTier(tier.id)} className="text-zinc-600 hover:text-red-400 transition-colors">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
            <form onSubmit={createTier} className="space-y-3">
              <Input data-testid="tier-name-input" required placeholder={t("dash_tier_name")} value={tierForm.name}
                onChange={(e) => setTierForm({ ...tierForm, name: e.target.value })} className="bg-zinc-950 border-zinc-800 text-white" />
              <Input data-testid="tier-desc-input" placeholder={t("dash_tier_desc")} value={tierForm.description}
                onChange={(e) => setTierForm({ ...tierForm, description: e.target.value })} className="bg-zinc-950 border-zinc-800 text-white" />
              <Input data-testid="tier-amount-input" required type="number" min="1" step="0.01" placeholder={t("dash_tier_price")} value={tierForm.amount_usd}
                onChange={(e) => setTierForm({ ...tierForm, amount_usd: e.target.value })} className="bg-zinc-950 border-zinc-800 text-white" />
              <Button data-testid="create-tier-btn" type="submit" className="w-full rounded-full bg-emerald-600 hover:bg-emerald-500 text-white">
                {t("dash_create_tier")}
              </Button>
            </form>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div data-testid="mic-queue-section" className="lg:col-span-2 bg-zinc-900 border border-emerald-900/40 rounded-2xl p-8">
          <h3 className="text-2xl font-semibold text-white mb-1 flex items-center gap-2">
            <Mic size={20} className="text-emerald-400" /> {t("queue_title")}
            {queue.length > 0 && (
              <span className="ml-2 text-xs font-bold bg-emerald-500 text-zinc-950 rounded-full px-2.5 py-0.5">{queue.length}</span>
            )}
          </h3>
          <p className="text-xs text-zinc-500 mb-2">{t("queue_sub")}</p>
          <p data-testid="queue-strike-note" className="text-xs text-sky-400/80 mb-6 flex items-start gap-1.5">
            <ShieldCheck size={13} className="mt-0.5 shrink-0" /> {t("queue_strike_note")}
          </p>
          {queue.length === 0 ? (
            <p className="text-sm text-zinc-500">{t("queue_empty")}</p>
          ) : (
            <div className="space-y-3">
              {queue.map((q, i) => (
                <div key={q.session_id} data-testid="queue-row" className="flex flex-col gap-3 bg-zinc-950 border border-zinc-800 rounded-xl p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-start gap-4 min-w-0">
                      <span className="text-lg font-black text-emerald-400 shrink-0 w-8">#{i + 1}</span>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-white truncate">{q.fan_name} <span className="text-zinc-500 font-normal">· {q.tier_name} · {fmtUsd(q.amount_cents)}</span></p>
                        {q.fan_message && <p className="text-xs text-emerald-300/80 italic mt-1">“{q.fan_message}”</p>}
                      </div>
                    </div>
                    <Button data-testid="queue-done-btn" size="sm" variant="outline"
                      className="rounded-full border-emerald-800/60 text-emerald-400 hover:bg-emerald-950/40 hover:text-emerald-300 bg-transparent shrink-0"
                      onClick={() => completeMic(q.session_id)}>
                      <CheckCircle2 size={14} className="mr-1.5" /> {t("queue_done")}
                    </Button>
                  </div>
                  <input data-testid="queue-proof-input" type="url" placeholder={t("queue_proof_ph")}
                    value={proofMap[q.session_id] || ""}
                    onChange={(e) => setProofMap((m) => ({ ...m, [q.session_id]: e.target.value }))}
                    className="w-full rounded-lg bg-zinc-900 border border-zinc-800 text-zinc-300 text-xs px-3 py-2 focus:outline-none focus:border-emerald-700" />
                </div>
              ))}
            </div>
          )}
        </div>

        <div data-testid="stream-history-section" className="bg-zinc-900 border border-zinc-800/60 rounded-2xl p-8">
          <h3 className="text-2xl font-semibold text-white mb-6 flex items-center gap-2">
            <History size={20} className="text-zinc-400" /> {t("history_title")}
          </h3>
          {history.length === 0 ? (
            <p className="text-sm text-zinc-500">{t("history_empty")}</p>
          ) : (
            <div className="space-y-3">
              {history.slice(0, 10).map((h) => (
                <div key={h.id} data-testid="history-row" className="border-b border-zinc-800/60 pb-3">
                  <p className="text-sm font-semibold text-white">{new Date(h.verified_at).toLocaleString(lang)}</p>
                  <p className="text-xs text-zinc-500 mt-1">
                    <span className="capitalize">{h.platform}</span> · {h.source === "auto" ? t("source_auto") : t("source_manual")} · <span className="text-emerald-400">{t("history_verified", { n: h.held_count ?? 0 })}</span>
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
